import React from 'react';
import { Form, Input } from 'antd';
import { BillingErrorsValues } from './models';
import SuggestSelectCities from 'lib.components/src/components/SuggestSelectCities';
import SuggestSelectCountries from 'lib.components/src/components/SuggestSelectCountries';
import { PatchBillingInfoUser } from 'lib.store/src/main/billing/models';
import { useForm } from 'laravel-precognition-react';
import { AddressInput } from 'lib.components/src';
import { useBreakpoints } from 'lib.hooks/src';
import { apiBaseUrl } from 'lib.shared/src/api';

interface BillingInfoProps {
  form: PatchBillingInfoUser;
  validationErrors: BillingErrorsValues;
  onFormChange: (field: Partial<PatchBillingInfoUser>) => void;
}

interface CityFieled {
  city: {
    country: {
      id: number;
      slug: string;
    } | null;

    postal_code?: string;
  };
}

const BillingForm: React.FC<BillingInfoProps> = ({ form, onFormChange, validationErrors }) => {
  const isDesktop = useBreakpoints().md;
  const billingForm = useForm(
    'patch',
    `${apiBaseUrl}/billing`,
    form as any as Record<string, unknown>,
  );
  const { errors } = billingForm;

  const formValuesHadler = (field: Partial<PatchBillingInfoUser>) => {
    if ('city' in field) {
      const values = field as unknown as CityFieled;
      onFormChange({ country: { ...(values?.city?.country || {}) } });
      onFormChange({ city: values.city });
      onFormChange({ address: values.address });
      onFormChange({ postal_code: values.postal_code });
      billingForm.setData('country', { ...(values?.city?.country || {}) });
      billingForm.setData('city', values.city);
      if ('address' in field) {
        billingForm.setData('address', values.address);
      }
      if ('postal_code' in field) {
        billingForm.setData('postal_code', values.postal_code);
      }
      billingForm.setErrors({ ...billingForm.errors, city: '' });
      return;
    }
    onFormChange(field);
    const entries = Object.entries(field);
    const [key, value] = entries?.[0];
    if (key) {
      billingForm.setData(key, value);
      billingForm.setErrors({ ...billingForm.errors, [key]: '' });
    }
  };

  const formSize = isDesktop ? 'middle' : 'large';

  return (
    <Form
      name="billingInfo"
      initialValues={form}
      autoComplete="off"
      layout="vertical"
      size={formSize}
      onValuesChange={(field: Partial<PatchBillingInfoUser>) => {
        formValuesHadler(field);
      }}
      className="common-form w-full"
    >
      <Form.Item
        help={errors?.name || (validationErrors?.name && validationErrors?.name)}
        validateStatus={validationErrors?.name || errors?.name ? 'error' : ''}
        name="name"
        label={isDesktop ? 'Billing contact' : undefined}
      >
        <Input
          placeholder="Martin Smith"
          onBlur={() => {
            billingForm.validate('name');
          }}
          data-test-id="billing-name-input"
        />
      </Form.Item>
      <Form.Item
        help={
          errors?.company_name || (validationErrors?.company_name && validationErrors?.company_name)
        }
        validateStatus={validationErrors?.company_name || errors?.company_name ? 'error' : ''}
        name="company_name"
        label={isDesktop ? 'Company name' : undefined}
      >
        <Input
          placeholder="Your company name"
          onBlur={() => {
            billingForm.validate('company_name');
          }}
          data-test-id="billing-company-input"
        />
      </Form.Item>
      <Form.Item
        help={
          errors?.country ||
          errors?.['country.id'] ||
          validationErrors?.country ||
          validationErrors?.['country.id']
        }
        validateStatus={
          errors?.country ||
          errors?.['country.id'] ||
          validationErrors?.country ||
          validationErrors?.['country.id']
            ? 'error'
            : ''
        }
        label={isDesktop ? 'Country' : undefined}
      >
        <SuggestSelectCountries
          value={form.country}
          placeholder={form?.country?.slug || 'Choose country'}
          onChange={(country) => {
            onFormChange({ country });
            onFormChange({ city: null });
            billingForm.setData('country', country);
            billingForm.setData('city', null);
            billingForm.setErrors({ ...billingForm.errors, country: '' });
          }}
          data-test-id="billing-form-country"
        />
      </Form.Item>
      <Form.Item
        help={
          errors?.['city.id'] || (validationErrors?.['city.id'] && validationErrors?.['city.id'])
        }
        validateStatus={errors?.['city.id'] || validationErrors?.['city.id'] ? 'error' : ''}
        label={isDesktop ? 'City' : undefined}
      >
        <SuggestSelectCities
          value={form?.city || null}
          countryValue={form.country}
          placeholder={form?.city?.slug || 'Choose city'}
          onChange={(city) => {
            onFormChange({ city });
            billingForm.setData('city', city);
            billingForm.setErrors({ ...billingForm.errors, city: '' });
          }}
          data-test-id="billing-form-city"
        />
      </Form.Item>

      <Form.Item
        help={
          errors?.postal_code || (validationErrors?.postal_code && validationErrors?.postal_code)
        }
        validateStatus={errors?.postal_code || validationErrors?.postal_code ? 'error' : ''}
        label={isDesktop ? 'Postal code' : undefined}
      >
        <Input
          value={form.postal_code}
          onChange={(e) => {
            formValuesHadler({ postal_code: e.target.value });
          }}
          placeholder="30702"
          onBlur={() => {
            billingForm.validate('postal_code');
          }}
          data-test-id="billing-postal-code-input"
        />
      </Form.Item>

      <Form.Item
        help={errors?.email || (validationErrors?.email && validationErrors?.email)}
        validateStatus={errors?.email || validationErrors?.email ? 'error' : ''}
        name="email"
        label={isDesktop ? 'Email' : undefined}
        rules={[{ type: 'email' }]}
      >
        <Input
          placeholder="Your email"
          onBlur={() => {
            billingForm.validate('email');
          }}
          data-test-id="billing-email-input"
        />
      </Form.Item>

      <Form.Item
        help={errors?.address || (validationErrors?.address && validationErrors?.address)}
        validateStatus={errors?.address || validationErrors?.address ? 'error' : ''}
        name="address"
        label={isDesktop ? 'Organization address' : undefined}
      >
        <AddressInput
          id="billingInfo_address"
          placeholder="Rua do Comércio, 123"
          value={form.address}
          onSelect={formValuesHadler}
          onBlur={() => {
            billingForm.validate('address');
          }}
          data-test-id="billing-address-input"
        />
      </Form.Item>

      <Form.Item
        help={errors?.tax_code || (validationErrors?.tax_code && validationErrors?.tax_code)}
        validateStatus={errors?.tax_code || validationErrors?.tax_code ? 'error' : ''}
        name="tax_code"
        label={isDesktop ? 'VAT number' : undefined}
      >
        <Input
          placeholder="PT648208176"
          type="numeric"
          onBlur={() => {
            billingForm.validate('tax_code');
          }}
          data-test-id="billing-vat-input"
        />
      </Form.Item>
    </Form>
  );
};

export default BillingForm;
